import { safeGet } from './HistogramDataProvider.js';
import { WithChildren } from '../../../core/types/with-children.js';
import type { ChartUnit } from '../../core/types/chart-unit.js';
import type { MinMax } from '../../core/types/min-max.js';
import { getRelativeValue } from '../../core/utils/get-relative-value.js';
import {
  CumulativePoint,
  getCumulativeValue,
  setCumulativeBins,
} from '../components/displays/utils/datapoint-stacking.js';
import { HistogramFilteredDataContext } from '../contexts/HistogramFilteredData.context.js';
import { useFilteredSeriesIdsState } from '../hooks/store/selectors.js';
import { useHistogramData } from '../hooks/useHistogramData.js';
import { useHistogramValueRepresentation } from '../hooks/useHistogramValueRepresentation.js';
import type {
  BinMetadata,
  HistogramSeries,
  WeakMapBins,
  WeakMapSeries,
} from '../types/histogram.js';
import { getMaxSeriesValue } from '../utils/calculate-values.js';

const calculateTotalValuePerSeries = (
  totalValues: Map<string, number>,
  key: string,
  value: number,
) => totalValues.set(key, (totalValues.get(key) ?? 0) + value);

const getUnits = (series: HistogramSeries[]) =>
  series.flatMap(({ unit }) =>
    typeof unit === 'string' ? unit : unit?.map(({ group }) => group),
  );

export const setMinMaxPerUnit = (
  minMaxPerUnit: Map<ChartUnit, MinMax>,
  unit: ChartUnit,
  binsCumulative: number,
) => {
  const currentMinMax = minMaxPerUnit.get(unit);

  if (currentMinMax) {
    minMaxPerUnit.set(unit, {
      min: Math.min(currentMinMax.min, binsCumulative),
      max: Math.max(currentMinMax.max, binsCumulative),
    });
  } else {
    minMaxPerUnit.set(unit, {
      min: Math.min(Infinity, binsCumulative),
      max: Math.max(-Infinity, binsCumulative),
    });
  }
};

export const useBuildFilteredMetadata = (
  series: HistogramSeries[],
  initSeriesMap: WeakMapSeries,
) => {
  const binsMap: WeakMapBins = new WeakMap();
  const filteredUnit: ChartUnit[] = [];

  const valueRepresentation = useHistogramValueRepresentation();

  const cumulativeStackedBins = new Map<string, CumulativePoint>();

  const minMaxPerUnit = new Map<ChartUnit, MinMax>();

  const minMaxXValue = { min: Infinity, max: -Infinity };

  const memoizedMaxSeriesValue = new Map();

  const isRelative = valueRepresentation === 'relative';

  const totalValues = new Map();

  const units = isRelative ? new Set(getUnits(series)) : new Set();

  if (isRelative) {
    series.forEach(({ bins }) =>
      bins.forEach(({ from, to, value }) =>
        calculateTotalValuePerSeries(totalValues, `${from}-${to}`, value),
      ),
    );
  }

  for (let i = 0; i < series.length; i++) {
    const singleSeries = series[i];
    const hasSingleSeries = memoizedMaxSeriesValue.has(singleSeries);
    const unit = isRelative ? '%' : singleSeries.unit;

    const unitIndex = filteredUnit.findIndex((u) => u === unit);

    const isNewUnit = unitIndex < 0;

    if (isNewUnit) {
      filteredUnit.push(unit);
    }

    const { color, id } = safeGet(initSeriesMap, singleSeries);

    const scaleIndex = isNewUnit ? filteredUnit.length - 1 : unitIndex;

    let totalValue;

    if (isRelative) {
      totalValue = hasSingleSeries
        ? memoizedMaxSeriesValue.get(singleSeries)
        : getMaxSeriesValue(singleSeries);

      if (!hasSingleSeries) {
        memoizedMaxSeriesValue.set(singleSeries, totalValue);
      }
    }

    for (let j = 0; j < singleSeries.bins.length; j++) {
      const bin = singleSeries.bins[j];
      const { value, to, from } = bin;

      if (isRelative && series.length > 1 && units.size === 1) {
        totalValue = totalValues.get(`${from}-${to}`);
      }

      const newValue = isRelative
        ? getRelativeValue(value, Math.abs(totalValue))
        : value;

      minMaxXValue.min = Math.min(minMaxXValue.min, from, to);
      minMaxXValue.max = Math.max(minMaxXValue.max, from, to);

      const cumulativeStack = getCumulativeValue(
        cumulativeStackedBins,
        bin,
        singleSeries.unit,
        id,
        newValue,
      );

      const binMetadata: BinMetadata = {
        seriesId: id,
        color,
        unit,
        value: newValue,
        from,
        to,
        seriesName: singleSeries.name,
        cumulativeValue: cumulativeStack.cumulativeValue,
        absolute: isRelative
          ? {
              value: bin.value,
              unit: singleSeries.unit,
            }
          : undefined,
      };

      setCumulativeBins(
        cumulativeStackedBins,
        id,
        cumulativeStack.key,
        cumulativeStack.binsCumulative,
        cumulativeStack.baseCumulative,
      );

      binsMap.set(bin, binMetadata);

      setMinMaxPerUnit(minMaxPerUnit, unit, cumulativeStack.binsCumulative);
    }

    const metadata = {
      id,
      color,
      scaleIndex,
    };

    initSeriesMap.set(singleSeries, metadata);
  }

  return {
    binsMap,
    seriesMap: initSeriesMap,
    filteredUnits: [...filteredUnit],
    minMaxPerUnit,
    minMaxXValue,
  };
};

export const HistogramFilteredDataProvider = ({ children }: WithChildren) => {
  const { series, seriesMap: initialSeriesMap } = useHistogramData();
  const filteredSeriesIds = useFilteredSeriesIdsState();

  const hasId = (series: HistogramSeries) =>
    filteredSeriesIds.has(safeGet(initialSeriesMap, series).id);

  const filteredHistogramSeries =
    filteredSeriesIds.size > 0 ? series.filter(hasId) : series;

  const { binsMap, seriesMap, filteredUnits, minMaxPerUnit, minMaxXValue } =
    useBuildFilteredMetadata(filteredHistogramSeries, initialSeriesMap);

  return (
    <HistogramFilteredDataContext.Provider
      value={{
        series: filteredHistogramSeries,
        seriesMap,
        binsMap,
        units: filteredUnits,
        minMaxPerUnit,
        minMaxXValue,
      }}
    >
      {children}
    </HistogramFilteredDataContext.Provider>
  );
};

HistogramFilteredDataProvider['displayName'] = 'HistogramFilteredDataProvider';
